import { DefaultTextAreaComponent } from "@constellation-academy/framework-react-components";
import { useFormik } from "formik";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import * as React from "react";
import { useSelector } from "react-redux";
import { useFragment, useMutation } from "react-relay";
import * as Yup from "yup";
import { useTranslation } from "@i18n/hooks";
import { type editRootExtensionForm_QueryFragment$key } from "@relay/editRootExtensionForm_QueryFragment.graphql";
import { type editRootExtensionForm_RootExtensionImplFragment$key } from "@relay/editRootExtensionForm_RootExtensionImplFragment.graphql";
import { type editRootExtensionForm_SetRootExtensionImplMutation } from "@relay/editRootExtensionForm_SetRootExtensionImplMutation.graphql";
import {
	QUERY_FRAGMENT,
	ROOT_EXTENSION_IMPL_FRAGMENT,
	SET_ROOT_EXTENSION_IMPL_MUTATION,
} from "./edit-root-extension-form.graphql";
import { videoInputContainerClass } from "./edit-root-extension-form.styles";
import {
	type EditRootExtensionFormProps,
	type EditRootExtensionFormState,
} from "./edit-root-extension-form.types";
import { DefaultTextFieldComponent } from "../../../../components/DefaultTextInput";
import { MemoizedRichTextEditor } from "../../../../components/rich-text-editor";
import { RichTextEditorToolbar } from "../../../../components/rich-text-editor/rich-text-editor.const";
import { ValidatedField } from "../../../../components/ValidatedField";
import { VideoSourceDropdown } from "../../../../components/video-source-dropdown";
import { GenericSelectProductFieldDialog } from "../../../../features/products/generic-select-product-field-dialog/generic-select-product-field-dialog.component";
import { SelectProductFieldDialog } from "../../../../features/products/select-product-field-dialog";
import { type Product } from "../../../../features/products/select-product-field-dialog/select-product-field-dialog.interface";
import {
	SelectAcademiesTagField,
	type SelectAcademiesTagFieldState,
} from "../../../../features/tags/select-academies-tag-field";
import {
	addEditedFormToEditedFormsArray,
	removeEditedFormFromEditedFormsArray,
} from "../../../../store/slices/CoreSlice";
import { selectActiveFeatureToggles } from "../../../../store/slices/FeatureTogglesSlice";
import { useTypedDispatch } from "../../../../store/store.redux";

export const EditRootExtensionForm = ({
	rootId,
	rootExtensionImplFragmentRef,
	queryFragmentRef,
}: EditRootExtensionFormProps) => {
	const activeFeatureToggles = useSelector(selectActiveFeatureToggles);
	const hasSplitCourseCertification = activeFeatureToggles?.includes("splitCourseCertification");

	const queryFragment = useFragment<editRootExtensionForm_QueryFragment$key>(
		QUERY_FRAGMENT,
		queryFragmentRef,
	);
	const extension = useFragment<editRootExtensionForm_RootExtensionImplFragment$key>(
		ROOT_EXTENSION_IMPL_FRAGMENT,
		rootExtensionImplFragmentRef,
	);

	const { t } = useTranslation("common");
	const dispatch = useTypedDispatch();
	const [editRootExtension, isEditingRootNodePart] =
		useMutation<editRootExtensionForm_SetRootExtensionImplMutation>(
			SET_ROOT_EXTENSION_IMPL_MUTATION,
		);

	const formId = "EditNodeExtensionForm";
	const formik = useFormik<EditRootExtensionFormState>({
		enableReinitialize: true,
		initialValues: {
			learnGoals: extension.learnGoalsOpt ?? undefined,
			trailerVimeoIdOpt: extension.trailerOpt,
			tags: extension.tags.map((t) => ({
				id: t.id,
				data: {
					name: t.data.name,
				},
			})) as SelectAcademiesTagFieldState,
			product: extension.product ?? undefined,
			diploma: extension.diploma,
			abbreviation: extension.abbreviation,
			courseContentProduct: extension.courseContentProduct ?? undefined,
			certificationProduct: extension.certificationProduct ?? undefined,
		},
		validationSchema: Yup.object().shape({
			prefixTitle: Yup.string(),
			price: Yup.number().optional(),
			supplier: Yup.string().optional(),
			abbreviation: Yup.string().optional(),
		}),
		onSubmit: (values, { setSubmitting }) => {
			editRootExtension({
				variables: {
					input: {
						rootId,
						learnGoals: values.learnGoals,
						trailerVimeoId: values.trailerVimeoIdOpt,
						tagIds: values.tags?.map((t) => t.id) ?? [],
						licenseProductId: values.product?.id,
						diploma: values.diploma,
						abbreviation: values.abbreviation,
						courseContentProductId: values.courseContentProduct?.id,
						certificationProductId: values.certificationProduct?.id,
					},
				},
				updater: (store) => {
					const rootRecord = store.get(rootId);
					if (rootRecord) rootRecord.setValue("Draft", "status");
				},
				onCompleted: () => {
					void formik.setTouched({});
					setSubmitting(false);
					dispatch(removeEditedFormFromEditedFormsArray({ form: formId }));
				},
			});
		},
	});
	const handleFormEdited = React.useCallback(
		() => dispatch(addEditedFormToEditedFormsArray({ form: formId })),
		[dispatch, formId],
	);

	return (
		<Card className="mb-2">
			<form onSubmit={formik.handleSubmit} className="p-fluid">
				<ValidatedField
					name={"diploma"}
					label={t("forms.diploma")}
					component={DefaultTextAreaComponent}
					onChange={handleFormEdited}
					formikConfig={formik}
				/>

				<ValidatedField<EditRootExtensionFormState, string>
					name={"abbreviation"}
					label={t("forms.abbreviation")}
					component={DefaultTextFieldComponent}
					onChange={() => dispatch(addEditedFormToEditedFormsArray({ form: formId }))}
					formikConfig={formik}
				/>

				<ValidatedField<EditRootExtensionFormState, string>
					name={"learnGoals"}
					label={t("forms.learningGoals")}
					component={(props) => (
						<MemoizedRichTextEditor
							{...props}
							height={200}
							toolbar={RichTextEditorToolbar.WithLists}
						/>
					)}
					onChange={handleFormEdited}
					formikConfig={formik}
				/>

				<div className={videoInputContainerClass}>
					<ValidatedField<EditRootExtensionFormState, string>
						name={"trailerVideoSourceIdOpt"}
						label={t("forms.videoSource")}
						onChange={() => dispatch(addEditedFormToEditedFormsArray({ form: formId }))}
						component={({ fieldValue, updateField, onChange }) => {
							return (
								<VideoSourceDropdown
									fieldValue={fieldValue}
									updateField={updateField}
									onChange={onChange}
								/>
							);
						}}
						formikConfig={formik}
					/>
					<ValidatedField<EditRootExtensionFormState, string>
						name={"trailerVimeoIdOpt"}
						label={t("forms.vimeoId")}
						component={DefaultTextFieldComponent}
						onChange={() => dispatch(addEditedFormToEditedFormsArray({ form: formId }))}
						formikConfig={formik}
					/>
				</div>
				<ValidatedField<EditRootExtensionFormState, SelectAcademiesTagFieldState>
					name={"tags"}
					label={t("forms.tags")}
					component={(renderConfig) => (
						<SelectAcademiesTagField
							queryFragmentRef={queryFragment}
							singleSelection
							{...renderConfig}
						/>
					)}
					onChange={() => dispatch(addEditedFormToEditedFormsArray({ form: formId }))}
					formikConfig={formik}
				/>
				<ValidatedField<EditRootExtensionFormState, Product>
					name={"product"}
					label={t("forms.product")}
					component={SelectProductFieldDialog}
					formikConfig={formik}
				/>

				{hasSplitCourseCertification && (
					<>
						<ValidatedField<EditRootExtensionFormState, Product>
							name={"courseContentProduct"}
							label={t("forms.courseContentProduct")}
							component={(props) => (
								<GenericSelectProductFieldDialog
									{...props}
									productType={{
										emptyText: t(
											"educationalOffer.noCourseContentProductSelected",
										),
										buttonLabel: t(
											"educationalOffer.selectCourseContentProduct",
										),
										dialogHeader: t(
											"educationalOffer.selectCourseContentProduct",
										),
										fieldLabel: t(
											"educationalOffer.selectOneCourseContentProduct",
										),
									}}
								/>
							)}
							formikConfig={formik}
						/>
						<ValidatedField<EditRootExtensionFormState, Product>
							name={"certificationProduct"}
							label={t("forms.certificationProduct")}
							component={(props) => (
								<GenericSelectProductFieldDialog
									{...props}
									productType={{
										emptyText: t(
											"educationalOffer.noCertificationProductSelected",
										),
										buttonLabel: t(
											"educationalOffer.selectCertificationProduct",
										),
										dialogHeader: t(
											"educationalOffer.selectCertificationProduct",
										),
										fieldLabel: t(
											"educationalOffer.selectOneCertificationProduct",
										),
									}}
								/>
							)}
							formikConfig={formik}
						/>
					</>
				)}
				<Button
					disabled={Object.entries(formik.touched).length === 0 || isEditingRootNodePart}
					type="submit"
					label={t("actions.save")}
					className="p-mt-2"
				/>
			</form>
		</Card>
	);
};
